import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Textarea } from "@/components/ui/Input";
import { cn } from "@/lib/cn";
import { fmtDate } from "@/lib/briefs";
import { CATEGORIES, useFeedbackActions, useMyFeedback, type FeedbackCategory } from "@/lib/feedback";

const LIMIT = 4000;

/**
 * The feedback entry point, next to the account area (house skill). A required
 * category with a hint each, a message with a counter near the limit, the page
 * captured silently, the button disabled while sending, the text kept on
 * failure, and My feedback with the unseen indicator.
 */
export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"new" | "mine">("new");
  const mine = useMyFeedback();
  const unseen = mine.data?.unseen ?? 0;
  const location = useLocation();

  // ?feedback=1 (from the update email) opens My feedback.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("feedback") === "1") {
      setTab("mine");
      setOpen(true);
    }
  }, [location.search]);

  return (
    <>
      <Button variant="ghost" size="icon" aria-label="Feedback" onClick={() => setOpen(true)} className="relative">
        <MessageSquarePlus size={16} />
        {unseen > 0 ? <span className="bg-primary absolute top-1.5 right-1.5 h-2 w-2 rounded-full" aria-label={`${unseen} updates`} /> : null}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Feedback">
        <div className="mb-3 flex gap-1" role="tablist">
          {(["new", "mine"] as const).map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={cn("rounded-md px-3 py-1.5 text-sm", tab === t ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground")}
            >
              {t === "new" ? "Send feedback" : `My feedback${unseen ? ` (${unseen})` : ""}`}
            </button>
          ))}
        </div>
        {tab === "new" ? <NewFeedback onSent={() => setTab("mine")} /> : <MyFeedback />}
      </Dialog>
    </>
  );
}

function NewFeedback({ onSent }: { onSent: () => void }) {
  const { submit } = useFeedbackActions();
  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [body, setBody] = useState("");
  const [sentMessage, setSentMessage] = useState<string | null>(null);

  if (sentMessage) {
    return (
      <div className="text-sm">
        <p>{sentMessage}</p>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={() => {
              setSentMessage(null);
              onSent();
            }}
          >
            See my feedback
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="space-y-3 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        if (!category || !body.trim()) return;
        submit.mutate(
          { category, body: body.trim(), page: `${window.location.pathname}${window.location.search}` },
          {
            onSuccess: () => {
              setSentMessage("Thanks — sent to the admin team, with a copy to your email. You can follow it under My feedback.");
              setBody("");
              setCategory(null);
            },
          },
        );
      }}
    >
      <fieldset>
        <legend className="text-muted-foreground mb-1.5 text-xs font-medium">Category</legend>
        <div className="grid grid-cols-2 gap-1.5">
          {CATEGORIES.map((c) => (
            <label
              key={c.key}
              className={cn(
                "cursor-pointer rounded-md border px-3 py-2",
                category === c.key ? "border-primary bg-accent text-accent-foreground" : "border-input hover:bg-secondary",
              )}
            >
              <input type="radio" name="category" value={c.key} className="sr-only" checked={category === c.key} onChange={() => setCategory(c.key)} autoFocus={c.key === "Bug"} />
              <span className="block font-medium">{c.key}</span>
              <span className="text-muted-foreground block text-xs">{c.hint}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <div>
        <label htmlFor="feedback-body" className="text-muted-foreground mb-1.5 block text-xs font-medium">
          Message
        </label>
        <Textarea id="feedback-body" value={body} maxLength={LIMIT} onChange={(e) => setBody(e.target.value)} placeholder="What happened, or what would help?" />
        {body.length > LIMIT - 500 ? (
          <div className="text-muted-foreground mt-1 text-right text-[11px]">
            {body.length}/{LIMIT}
          </div>
        ) : null}
      </div>
      {submit.isError ? <p className="text-destructive text-xs">{(submit.error as Error).message}</p> : null}
      <div className="flex justify-end">
        <Button type="submit" disabled={!category || !body.trim() || submit.isPending}>
          {submit.isPending ? "Sending…" : "Send"}
        </Button>
      </div>
    </form>
  );
}

function MyFeedback() {
  const mine = useMyFeedback();
  const { seen } = useFeedbackActions();
  if (mine.isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (mine.isError) return <p className="text-destructive text-sm">Could not load your feedback.</p>;
  if (mine.data.feedback.length === 0) return <p className="text-muted-foreground text-sm">Nothing sent yet.</p>;
  return (
    <ul className="max-h-[60vh] space-y-2 overflow-y-auto text-sm">
      {mine.data.feedback.map((f) => (
        <li
          key={f.id}
          className={cn("border-border rounded-md border p-3", f.raiserUnseen && "border-primary")}
          onMouseEnter={() => f.raiserUnseen && seen.mutate(f.id)}
          onClick={() => f.raiserUnseen && seen.mutate(f.id)}
        >
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="font-medium">{f.category}</span>
            <span className={cn("rounded-full px-2 py-0.5", f.status === "New" ? "bg-secondary" : "bg-accent text-accent-foreground")}>
              {f.status}
              {f.raiserUnseen ? " · updated" : ""}
            </span>
          </div>
          <p className="mt-1 whitespace-pre-wrap">{f.body}</p>
          {f.adminRemark ? (
            <p className="bg-secondary/60 mt-2 rounded px-2 py-1.5 text-xs">
              <span className="text-muted-foreground">Reply: </span>
              {f.adminRemark}
            </p>
          ) : null}
          <div className="text-muted-foreground mt-1 text-[11px]">{fmtDate(f.createdAt)}</div>
        </li>
      ))}
    </ul>
  );
}
